// @ds/forms/DatePickerField
//
// Forms tier · Typed date input that bridges AntD DatePicker with Supabase's
// YYYY-MM-DD string format.
//
// AntD wrap justification (Rule 5):
//   1. Foot-gun: AntD DatePicker expects a Dayjs value; Supabase stores plain
//      date strings ("2024-01-31"). Every consumer would need the same
//      Dayjs ↔ string conversion boilerplate — this wrapper centralises it
//      and hides the foot-gun.
//   2. Non-trivial I/O surface: the component accepts `string | Dayjs` and
//      emits `string | null`, which is cleaner than leaking Dayjs to callers.
//
// All visual values come from AntD's theme driven by @ds/tokens/antdTheme —
// there are no inline colours or spacing overrides here. `width: "100%"` is
// a layout instruction, not a visual value.

import React from "react";
import { DatePicker, type DatePickerProps } from "antd";
import dayjs, { type Dayjs } from "dayjs";

// ─── Inputs ───────────────────────────────────────────────────────────────────

export interface DatePickerFieldInput
  extends Omit<DatePickerProps, "value" | "onChange"> {
  /** Date as a YYYY-MM-DD string (Supabase format) or a Dayjs instance. */
  value?: string | Dayjs;
  /** Passed to the root DatePicker's `data-testid`. */
  testId?: string;
}

// ─── Outputs ──────────────────────────────────────────────────────────────────

export interface DatePickerFieldOutput {
  /** Emits YYYY-MM-DD or null when cleared. */
  onChange?: (value: string | null) => void;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export type DatePickerFieldProps = DatePickerFieldInput & DatePickerFieldOutput;

// ─── Component ────────────────────────────────────────────────────────────────

export const DatePickerField: React.FC<DatePickerFieldProps> = ({
  value,
  onChange,
  testId,
  format = "YYYY-MM-DD",
  ...props
}) => {
  // Convert string value to Dayjs for the DatePicker
  const dayjsValue = value
    ? typeof value === "string"
      ? dayjs(value)
      : value
    : undefined;

  // Handle change and convert Dayjs back to string format
  const handleChange = (date: Dayjs | null) => {
    onChange?.(date ? date.format("YYYY-MM-DD") : null);
  };

  return (
    <DatePicker
      {...props}
      value={dayjsValue}
      onChange={handleChange}
      format={format}
      style={{ width: "100%", ...props.style }}
      data-testid={testId}
    />
  );
};
